import React, { useEffect, useState } from 'react';
import api from '../api';
import theme from '../theme';
import { Card, Button, Input, Select, Spinner, Empty, Badge, MaskedAccount, MaskedSortCode, ConfirmDialog } from '../components/common';

export default function Beneficiaries() {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deactivateId, setDeactivateId] = useState(null);
  const [revealed, setRevealed] = useState({});

  const [form, setForm] = useState({ name: '', sortCode: '', accountNumber: '', scheme: 'FPS', nickname: '' });

  const load = () => {
    api.get('/beneficiaries').then(setBeneficiaries).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post('/beneficiaries', {
        name: form.name,
        sortCode: form.sortCode.replace(/-/g, ''),
        accountNumber: form.accountNumber,
        scheme: form.scheme,
        nickname: form.nickname || undefined,
      });
      setShowForm(false);
      setForm({ name: '', sortCode: '', accountNumber: '', scheme: 'FPS', nickname: '' });
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await api.post(`/beneficiaries/${deactivateId}/deactivate`);
      setDeactivateId(null);
      load();
    } catch (err) {
      setError(err.message);
      setDeactivateId(null);
    }
  };

  const toggleReveal = (id) => {
    setRevealed(r => {
      const next = { ...r, [id]: !r[id] };
      if (next[id]) setTimeout(() => setRevealed(rv => ({ ...rv, [id]: false })), 30000);
      return next;
    });
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px' }}><Spinner size={32} /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '20px', margin: 0 }}>Beneficiaries</h1>
        <Button variant="accent" onClick={() => setShowForm(true)}>Add beneficiary</Button>
      </div>

      {error && (
        <Card style={{ background: '#FED7D7', borderColor: theme.colors.danger, marginBottom: '16px' }}>
          <div style={{ color: '#742A2A', fontSize: '14px' }}>{error}</div>
        </Card>
      )}

      {/* Add form */}
      {showForm && (
        <Card style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>New beneficiary</h3>
          <form onSubmit={handleSubmit}>
            <Input label="Account name" placeholder="e.g. Acme Supplies Ltd" value={form.name} onChange={e => set('name', e.target.value)} />
            <Input label="Nickname (optional)" placeholder="e.g. Acme" value={form.nickname} onChange={e => set('nickname', e.target.value)} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Input label="Sort code" placeholder="12-34-56" value={form.sortCode} onChange={e => set('sortCode', e.target.value)} />
              <Input label="Account number" placeholder="12345678" value={form.accountNumber} onChange={e => set('accountNumber', e.target.value)} />
            </div>
            <Select label="Scheme" value={form.scheme} onChange={e => set('scheme', e.target.value)}>
              <option value="FPS">Faster Payments</option>
              <option value="BACS">BACS</option>
              <option value="CHAPS">CHAPS</option>
            </Select>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <Button variant="ghost" type="button" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button variant="primary" loading={saving} disabled={!form.name || !form.sortCode || !form.accountNumber}>Save beneficiary</Button>
            </div>
          </form>
        </Card>
      )}

      {/* List */}
      {beneficiaries.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {beneficiaries.map(b => (
            <Card key={b.id} style={{ opacity: b.status === 'INACTIVE' ? 0.5 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '15px' }}>{b.nickname || b.name}</div>
                  {b.nickname && b.nickname !== b.name && (
                    <div style={{ fontSize: '13px', color: theme.colors.textLight }}>{b.name}</div>
                  )}
                  <div style={{ fontSize: '13px', color: theme.colors.textLight, marginTop: '4px' }}
                    onClick={() => toggleReveal(b.id)}
                  >
                    <MaskedSortCode value={b.sortCode} revealed={revealed[b.id]} onToggle={() => toggleReveal(b.id)} />
                    {' \u00b7 '}
                    <MaskedAccount value={b.accountNumber} revealed={revealed[b.id]} onToggle={() => toggleReveal(b.id)} />
                  </div>
                  <Badge color={b.status === 'ACTIVE' ? 'success' : 'default'} style={{ marginTop: '8px' }}>{b.status}</Badge>
                </div>
                {b.status === 'ACTIVE' && (
                  <Button variant="ghost" size="sm" onClick={() => setDeactivateId(b.id)}>Deactivate</Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Empty message="No beneficiaries saved" />
      )}

      <ConfirmDialog
        open={!!deactivateId}
        title="Deactivate beneficiary"
        message="This beneficiary will no longer be available for payments. You can re-add them later."
        confirmLabel="Deactivate"
        danger
        onConfirm={handleDeactivate}
        onCancel={() => setDeactivateId(null)}
      />
    </div>
  );
}
